import { ReportSourceAPI } from '@business/api/ReportSource';
import { ReportSourceId, ReportSourceRequest, ReportSourceResponse } from '@business/entities/ReportSource';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';

export class ReportSourceBloc extends BaseBloc {
  private readonly reportSourceRepo = new Repository<ReportSourceResponse>({
    getItemId: item => item.id,
  });

  readonly items$ = this.reportSourceRepo.items$;
  readonly selectItem = this.reportSourceRepo.selectItem;

  onReset() {
    this.reportSourceRepo.reset();
  }

  fetchSources = () => {
    return ReportSourceAPI.fetchAllItems().pipe(
      this.reportSourceRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_report_source_error') }),
    );
  };

  fetchSource = (id: ReportSourceId) => {
    return ReportSourceAPI.fetchItem(id).pipe(
      this.reportSourceRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_report_source_error') }),
    );
  };

  createSource = (data: Partial<ReportSourceRequest>) => {
    return ReportSourceAPI.createItem(data).pipe(
      this.reportSourceRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_report_source'),
      }),
    );
  };

  validateQuery = (query: string) => {
    return ReportSourceAPI.validateQuery(query).pipe(
      handleMessage({
        type: requestMessage('create_report_source'),
      }),
    );
  };

  updateSource = (id: ReportSourceId, data: Partial<ReportSourceRequest>) => {
    return ReportSourceAPI.updateItem(id, data).pipe(
      this.reportSourceRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_report_source'),
      }),
    );
  };

  loadDataFromURL = (file: File) => {
    return ReportSourceAPI.loadDataFromURL(file);
  };

  deleteSource = (id: ReportSourceId) => {
    return showDeleteConfirmation('Delete Source', 'Do you really want to remove this source?').pipe(
      switchMap(() => ReportSourceAPI.deleteItem(id)),
      this.reportSourceRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_report_source'),
      }),
    );
  };
}
